import { useState, type ComponentProps } from 'react';
import { Menu } from '@base-ui/react/menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistance } from 'date-fns';
import type {
  AgentChatFailure,
  AgentChatMessage,
} from '@/modules/use-agent-dialog';
import {
  addJournalNote,
  deleteJournalEntry,
  listJournalSaves,
  type JournalSave,
} from '@/modules/personal-agent-api';
import { showNotification } from '@/modules/show-notification';
import { zAgentMessageMenu } from '@/shared/constants/zIdxs';
import AnswerMarkdown from '@/shared/ui/AnswerMarkdown';
import Button from '@/shared/ui/Button';
import SpinnerSan from '@/shared/ui/SpinnerSan';
import assistantRateLimitCopy from '@/shared/utils/assistantRateLimitCopy';
import { track } from '@/shared/utils/track';
import bookSvg from './icons/book.svg';
import copySvg from './icons/copy.svg';
import editSvg from './icons/edit-2.svg';
import trashSvg from './icons/trash.svg';

export type AgentSurface = 'widget' | 'market';

// The scroll container and the input stay with the host panel; the menu's
// journal actions live here because every agent panel shares them.
export default function AgentThread({
  messages,
  busy,
  failure,
  now,
  surface,
  pair,
  onEdit,
  onRetrySend,
  onRetryReply,
  onRetryLoad,
}: {
  messages: AgentChatMessage[];
  busy: 'loading' | 'streaming' | null;
  failure: AgentChatFailure | null;
  now: number;
  surface: AgentSurface;
  pair: string | null;
  onEdit: (message: { id: string; text: string }) => void;
  onRetrySend: (question: string) => void;
  onRetryReply: () => void;
  onRetryLoad: () => void;
}) {
  const journal = useJournalSaves(messages, pair, surface);

  if (busy === 'loading') {
    return (
      <div className="grid flex-1 place-items-center">
        <SpinnerSan size={32} />
      </div>
    );
  }

  const lastMessage = messages.at(-1);
  const thinking =
    busy === 'streaming' &&
    (lastMessage?.role !== 'assistant' || lastMessage.text === '');

  return (
    <div className="flex flex-col gap-2xl">
      {messages.map((message, i) =>
        message.role === 'user' ?
          <UserMessage
            key={message.id ?? message.role}
            message={message}
            now={now}
            journal={journal}
            onEdit={busy === null ? onEdit : null}
          />
        : (
          busy === 'streaming' &&
          i === messages.length - 1 &&
          message.text === ''
        ) ?
          null
        : <div key={message.id ?? message.role} className="w-full">
            <AnswerMarkdown compact>{message.text}</AnswerMarkdown>
          </div>,
      )}
      {thinking && (
        <p
          className="text-body-md text-gray"
          style={{ animation: 'ask-pulse 1.2s ease-in-out infinite' }}
        >
          Thinking…
        </p>
      )}
      {failure !== null && (
        <div className="flex w-full flex-col items-start gap-m">
          <p className="text-body-md text-gray">{failureCopy(failure)}</p>
          {(failure.kind === 'load' ||
            (failure.kind !== 'edit' && failure.reason === 'error')) && (
            <Button
              size="sm"
              onClick={() => {
                if (failure.kind === 'load') onRetryLoad();
                else if (failure.kind === 'send') onRetrySend(failure.question);
                else onRetryReply();
              }}
            >
              Try again
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

type JournalActions = {
  entryIdOf: (messageId: string) => string | null;
  save: (message: { id: string; text: string }) => void;
  remove: (target: { messageId: string; entryId: string }) => void;
  pending: boolean;
};

// Keyed by the ids on screen, so a new turn refetches and a reopened dialog
// starts clean; a confirmed save or removal is written in place — no second
// read, the server just told us. The History list needs no invalidation: it
// refetches on every open.
function useJournalSaves(
  messages: AgentChatMessage[],
  pair: string | null,
  surface: AgentSurface,
): JournalActions {
  const queryClient = useQueryClient();
  const messageIds = messages.flatMap((m) =>
    m.role === 'user' && m.id !== null ? [m.id] : [],
  );
  const queryKey = ['agent-journal-saves', messageIds] as const;
  const saves = useQuery({
    queryKey,
    queryFn: () => listJournalSaves(messageIds),
    enabled: messageIds.length > 0,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
  const entryIds = new Map(
    (saves.data ?? []).map((s) => [s.messageId, s.entryId] as const),
  );

  const save = useMutation({
    mutationFn: (message: { id: string; text: string }) =>
      addJournalNote({
        content: message.text,
        pair,
        sourceMessageId: message.id,
      }),
    onSuccess: ({ entryId }, message) => {
      queryClient.setQueryData<JournalSave[]>(queryKey, (prev = []) => [
        ...prev.filter((s) => s.messageId !== message.id),
        { entryId, messageId: message.id },
      ]);
      track('ai_message_saved_to_journal', { surface });
      showNotification('info', 'Saved to journal');
    },
    onError: () =>
      showNotification(
        'error',
        'Could not save to journal — please try again.',
      ),
  });
  const remove = useMutation({
    mutationFn: (target: { messageId: string; entryId: string }) =>
      deleteJournalEntry(target.entryId),
    onSuccess: (_, target) => {
      queryClient.setQueryData<JournalSave[]>(queryKey, (prev = []) =>
        prev.filter((s) => s.messageId !== target.messageId),
      );
      track('ai_message_removed_from_journal', { surface });
      showNotification('info', 'Removed from journal');
    },
    onError: () =>
      showNotification(
        'error',
        'Could not remove from journal — please try again.',
      ),
  });

  return {
    entryIdOf: (messageId) => entryIds.get(messageId) ?? null,
    save: (message) => save.mutate(message),
    remove: (target) => remove.mutate(target),
    pending: save.isPending || remove.isPending,
  };
}

function UserMessage({
  message,
  now,
  journal,
  onEdit,
}: {
  message: AgentChatMessage;
  now: number;
  journal: JournalActions;
  onEdit: ((message: { id: string; text: string }) => void) | null;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const messageId = message.id;
  const entryId = messageId === null ? null : journal.entryIdOf(messageId);

  return (
    <div className="flex flex-col items-end self-end">
      <div className="w-71.75 max-w-full rounded-lg bg-background-main px-l py-s">
        <p className="text-body-md font-medium [word-break:break-word] text-gray-text">
          {message.text}
        </p>
      </div>
      <div className="flex items-center gap-l self-end">
        <p className="text-body-sm font-medium text-gray">
          {formatDistance(new Date(message.createdAt), now, {
            addSuffix: true,
          })}
        </p>
        <Menu.Root modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
          <Menu.Trigger
            className="btn-reset grid size-6 place-items-center text-gray"
            aria-label="Message actions"
          >
            <span className="flex gap-0.5">
              <span className="size-0.5 rounded-full bg-current" />
              <span className="size-0.5 rounded-full bg-current" />
              <span className="size-0.5 rounded-full bg-current" />
            </span>
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Positioner
              side="bottom"
              align="end"
              style={{ zIndex: zAgentMessageMenu }}
            >
              <Menu.Popup className="flex flex-col rounded-lg bg-background-secondary p-xs shadow-[0px_0px_2px_rgba(0,0,0,0.1)] outline-none">
                <MenuRow
                  icon={editSvg}
                  label="Edit"
                  disabled={messageId === null || onEdit === null}
                  onClick={(event) => {
                    // Base UI's list navigation refocuses the pressed item
                    // after this handler, which would take the focus the
                    // composer just received: the menu's own click handling
                    // is skipped and the menu closed by hand.
                    event.preventBaseUIHandler();
                    setMenuOpen(false);
                    if (messageId !== null && onEdit !== null)
                      onEdit({ id: messageId, text: message.text });
                  }}
                />
                <MenuRow
                  icon={copySvg}
                  label="Copy"
                  onClick={() => {
                    void navigator.clipboard.writeText(message.text);
                    showNotification('info', 'Copied');
                  }}
                />
                {messageId !== null && entryId !== null ?
                  <MenuRow
                    icon={trashSvg}
                    label="Remove from journal"
                    disabled={journal.pending}
                    onClick={() => journal.remove({ messageId, entryId })}
                  />
                : <MenuRow
                    icon={bookSvg}
                    label="Save to journal"
                    disabled={messageId === null || journal.pending}
                    onClick={() => {
                      if (messageId !== null)
                        journal.save({ id: messageId, text: message.text });
                    }}
                  />
                }
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      </div>
    </div>
  );
}

function MenuRow({
  icon,
  label,
  ...item
}: {
  icon: string;
  label: string;
} & Pick<ComponentProps<typeof Menu.Item>, 'disabled' | 'onClick'>) {
  return (
    <Menu.Item
      className="btn-reset flex items-center gap-2.5 px-2.5 py-s data-disabled:cursor-not-allowed data-disabled:opacity-50"
      {...item}
    >
      <span
        className="block size-4"
        style={{
          backgroundColor: 'var(--gray-text)',
          mask: `url(${JSON.stringify(icon)}) no-repeat center / contain`,
          WebkitMask: `url(${JSON.stringify(icon)}) no-repeat center / contain`,
        }}
      />
      <span className="text-body-md font-medium whitespace-nowrap text-gray-text">
        {label}
      </span>
    </Menu.Item>
  );
}

function failureCopy(failure: AgentChatFailure): string {
  if (failure.kind === 'load')
    return 'Could not load this conversation — please try again.';
  if (failure.reason === 'rate_limit')
    return assistantRateLimitCopy(failure.retryAfterSeconds);
  if (failure.reason === 'busy')
    return 'The assistant is still finishing the previous answer — try again in a moment.';
  if (failure.kind === 'edit')
    return 'Could not edit this message — please try again.';
  return 'The assistant is unavailable right now — please try again.';
}
